import React, { FC, useState, useEffect } from "react";
import styled from "styled-components";
import Section from "components/kaleido/parts/Section";
import AnchorTarget from "components/official/AnchorTarget";
import Headline from "components/kaleido/long/parts/Headline";
import { Back, Back2 } from "./parts/BackStyles";
import useObserver from "lib/hooks/kaleido/useObserver";
import Modal from "components/kaleido/long/parts/Modal";
import useAnnouncementModal from "lib/hooks/kaleido/useAnnouncementModal";
import { LpAnnouncementService } from "lib/service/client/LpAnnouncementService"
import { AnnouncementContentLink } from "./parts/AnnouncementContentLink";

export type LpAnnouncement = {
  id: number;
  announce_at: string;
  title: string;
  content: string;
  heading: string;
  prioritizes: boolean;
};

const Announcement: FC = () => {
  const { selectedAnnouncement, isModalOpen, openModal, closeModal } = useAnnouncementModal();
  const [visibleSection, setVisibleSection] = useState<string | null>(null);
  const sectionVisibilityFlags = useObserver(setVisibleSection);
  const isVisible = visibleSection === "announcement" || sectionVisibilityFlags["announcement"];
  const [announcements, setAnnouncements] = useState<LpAnnouncement[]>([]);
  useEffect(() => {
    const fetchAnnouncements = async () => {
      const service = new LpAnnouncementService();
      const code = "kaleido_hp";
      const result = await service.getLatestAnnouncements(code);
      if (result.isSuccess()) {
        setAnnouncements(result.value.announcements);
      } else {
        console.error("お知らせの取得に失敗しました");
      }
    };

    fetchAnnouncements();
  }, []);
  //お知らせのHTMLに<!--フラグ-->があるものを初期表示で強制的に表示させる
  useEffect(() => {
    if (announcements.length > 0) {
      const autoOpenMarker = '<!--フラグ-->';
      const targetAnnouncement = announcements.find(announcement => 
        announcement.content.includes(autoOpenMarker)
      );
      if (targetAnnouncement) {
        openModal(targetAnnouncement);
      }
    }
  }, [announcements]);

  return (
    <>
      <Back alt="背景画像" />
      <Back2 alt="背景画像2" />
      <StyledSection className={`section ${isVisible ? "visible" : "hidden"}`} id="announcement">
        <AnchorTarget id="announcement" />
        <Headline src={"/image/lp/kaleido/announcement/head.png"} alt="お知らせ" />
        <Content>
          <DescriptionWrapper>
            <Description>
              {announcements.map((announcement, index) => (
                <React.Fragment key={index}>
                  <AnnouncementContentLink announcement={announcement}>
                    <AnnouncementItem>
                      <HeaderArea>
                        {announcement.heading && <HeadingArea>{announcement.heading}</HeadingArea>}
                        <DateArea>{announcement.announce_at}</DateArea>
                      </HeaderArea>
                      <TitleArea className="title-area">{announcement.title}</TitleArea>
                    </AnnouncementItem>
                  </AnnouncementContentLink>
                  {index < announcements.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Description>
          </DescriptionWrapper>
        </Content>
      </StyledSection>

      {isModalOpen && selectedAnnouncement && (
        <Modal content={selectedAnnouncement.content} onClose={closeModal} />
      )}
    </>
  );
};

export default Announcement;

const StyledSection = styled(Section)`
  transition: opacity 0.5s ease-in-out;
  opacity: 0;
  &.visible {
    opacity: 1;
  }
  &.hidden {
    opacity: 0;
  }
  margin-bottom: 12vw;
  overflow: hidden;

  @media screen and (min-width: 1281px) {
    margin-bottom: 160px;
  }
`;
const Content = styled.div`
  margin: 0 auto;
  @media screen and (max-width: 960px) {
    padding: 0 20px;
  }
  overflow: hidden;

  width: 90%;
  @media screen and (min-width: 1281px) {
    width: 95%;
  }
`;
const DescriptionWrapper = styled.div`
  position: relative;
`;
const Description = styled.div`
  display: flex;
  flex-direction: column;
`;
const AnnouncementItem = styled.div`
  padding: 20px 10px 20px 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media screen and (max-width: 280px) {
    height: 80px;
  }
  @media screen and (min-width: 281px) {
    height: 88px;
  }
  @media screen and (min-width: 361px) {
    height: 95px;
  }
  @media screen and (min-width: 501px) {
    min-height: 110px;
  }
  @media screen and (min-width: 961px) {
    min-height: 125px;
  }
`;
const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;
`;
const HeadingArea = styled.div`
  background-color: #f1889b;
  color: white;
  padding: 5px 10px;
  font-weight: bold;
  display: inline-block;
  margin-right: 10px;

  @media screen and (max-width: 360px) {
    font-size: 0.9em;
  }
  @media screen and (min-width: 361px) {
    font-size: 1em;
  }
  @media screen and (min-width: 390px) {
    font-size: 1.1em;
  }
  @media screen and (min-width: 551px) {
    font-size: 1.2em;
  }
`;
const DateArea = styled.div`
  color: #f1889b;
  margin-left: 10px;

  @media screen and (max-width: 360px) {
    font-size: 0.8em;
  }
  @media screen and (min-width: 361px) {
    font-size: 0.9em;
  }
  @media screen and (min-width: 390px) {
    font-size: 1em;
  }
  @media screen and (min-width: 551px) {
    font-size: 1.1em;
  }
`;
const TitleArea = styled.div`
  color: #333333;
  font-weight: bold;
  margin-left: 15px;
  padding 15px 0px;

  @media screen and (max-width: 360px) {
    font-size: 0.9em;
    padding 5px 0px;
  }
  @media screen and (min-width: 361px) {
    font-size: 0.93em;
    padding 5px 0px;
  }
  @media screen and (min-width: 551px) {
    font-size: 1.2em;
  }
`;
const Divider = styled.div`
  height: 2px;
  background-color:  #c8a94b;
  margin: 0;
`;
export const AnnouncementLink = styled.a`
  color: inherit;
  display: block;
  .title-area {
    color: #1a0dab;
    text-decoration: underline;
    transition: opacity 0.2s ease;
  }
`;
